/**
 * Deep Clone Service
 *
 * Safely deep clones any value using structuredClone with JSON fallback.
 * Handles circular references and non-serializable values gracefully.
 */

import { createModuleLogger } from './logger';

const logger = createModuleLogger('DeepCloneService');

/**
 * Deep clone a value using the most appropriate method.
 * Falls back from structuredClone to JSON stringify/parse to reference return.
 *
 * @param value The value to clone
 * @returns A deep clone of the value
 */
export function deepClone<T>(value: T): T {
  // Try native structuredClone first (handles circular refs, typed arrays, dates, etc.)
  try {
    return structuredClone(value);
  } catch (error) {
    // Fall back to JSON serialization for most objects
    try {
      return JSON.parse(JSON.stringify(value)) as T;
    } catch (innerError) {
      // Last resort: return reference and warn
      logger.warn(
        `[deepClone] Could not clone value, returning reference. Type: ${typeof value}, Error: ${innerError}`,
      );
      return value;
    }
  }
}

/**
 * Check if two values are deep equal (for diff purposes).
 * Uses JSON string comparison as a fast heuristic.
 *
 * @param a First value
 * @param b Second value
 * @returns true if values are deep equal
 */
export function deepEqual(a: unknown, b: unknown): boolean {
  try {
    return JSON.stringify(a) === JSON.stringify(b);
  } catch {
    // If serialization fails, consider them not equal
    return false;
  }
}
